/**
 * Architectural Lint engine (doc 147 §6.2).
 *
 * Generalises the doc-129 §21 drift gates: each rule has an id, severity,
 * enabled flag, and a pure validator function. The engine runs all enabled
 * rules and aggregates findings; consumers (`atlas-lint` CLI, the
 * LintDashboardView) group/filter by severity.
 *
 * Configuration is currently static TypeScript (no YAML dependency). The
 * `LintConfig` overlay lets a caller toggle rules or change severities
 * without forking the rule list — equivalent in expressiveness to a
 * `lint.yaml` file.
 */
import type { AtlasManifest } from '../types.js';
import { phantomRefs } from '../validators/phantomRefs.js';
import { pathWithoutSchema } from '../validators/pathWithoutSchema.js';
import { surfaceWithoutPayload } from '../validators/surfaceWithoutPayload.js';
import { refCycle } from './refCycle.js';
import { godEntity } from './godEntity.js';
import { piiUntagged } from './piiUntagged.js';
import { namingMix } from './namingMix.js';
import { orphanPath } from './orphanPath.js';
import { unbridgedCrossDomain } from './unbridgedCrossDomain.js';
import { deadEvent, deadCloudFunction } from './deadCode.js';
import { missingFsmCoverage } from './missingFsmCoverage.js';

export type LintSeverity = 'high' | 'medium' | 'low';

export interface LintRule {
  id: string;
  severity: LintSeverity;
  enabled: boolean;
  description: string;
  run: (manifest: AtlasManifest) => Array<{ ref: string; message: string }>;
}

export interface LintFinding {
  ruleId: string;
  severity: LintSeverity;
  ref: string;
  message: string;
}

export interface LintReport {
  findings: LintFinding[];
  /** Findings grouped by severity, in [high, medium, low] order. */
  bySeverity: Record<LintSeverity, LintFinding[]>;
  /** Top-level pass/fail — non-zero high findings fail CI by default. */
  ok: boolean;
}

export interface LintConfig {
  /** Override per-rule enabled flag. */
  enabled?: Record<string, boolean>;
  /** Override per-rule severity. */
  severity?: Record<string, LintSeverity>;
  /** Severity threshold that fails CI. Default 'high'. */
  failOn?: LintSeverity;
}

export const LINT_RULES: readonly LintRule[] = [
  {
    id: 'phantom-ref',
    severity: 'high',
    enabled: true,
    description: 'Field references a type that does not resolve to an entity, enum, or known scalar.',
    run: (m) =>
      phantomRefs(m).map((i) => ({ ref: i.ref, message: i.message })),
  },
  {
    id: 'path-without-schema',
    severity: 'high',
    enabled: true,
    description: 'Firestore path declared without a backing entity schema.',
    run: (m) => pathWithoutSchema(m).map((i) => ({ ref: i.ref, message: i.message })),
  },
  {
    id: 'surface-without-payload',
    severity: 'medium',
    enabled: true,
    description: 'Surface declared without a payload schema — SDUI render contract gap.',
    run: (m) =>
      surfaceWithoutPayload(m).map((i) => ({ ref: i.ref, message: i.message })),
  },
  {
    id: 'ref-cycle',
    severity: 'high',
    enabled: true,
    description: 'Reference cycle in the entity graph — refactor candidates or document the intentional ownership inversion.',
    run: refCycle,
  },
  {
    id: 'god-entity',
    severity: 'medium',
    enabled: true,
    description: 'Entity has too many fields or incoming references — extract sub-entities.',
    run: godEntity,
  },
  {
    id: 'pii-untagged',
    severity: 'high',
    enabled: true,
    description: 'Field name suggests PII but the entity has no pii:* tag.',
    run: piiUntagged,
  },
  {
    id: 'naming-mix',
    severity: 'low',
    enabled: true,
    description: 'Entity mixes snake_case + camelCase field names — pick a convention.',
    run: namingMix,
  },
  {
    id: 'orphan-path',
    severity: 'high',
    enabled: true,
    description: 'Firestore path entity id does not resolve.',
    run: orphanPath,
  },
  {
    id: 'unbridged-cross-domain',
    severity: 'medium',
    enabled: true,
    description: 'Reference crosses domain boundaries without a bridge:* tag on the source entity.',
    run: unbridgedCrossDomain,
  },
  {
    id: 'dead-event',
    severity: 'medium',
    enabled: true,
    description: 'Event has no carriedEntities — likely orphaned spec.',
    run: deadEvent,
  },
  {
    id: 'dead-cf',
    severity: 'medium',
    enabled: true,
    description: 'Cloud Function has no documented reads or writes — coverage gap.',
    run: deadCloudFunction,
  },
  {
    id: 'missing-fsm-coverage',
    severity: 'medium',
    enabled: true,
    description: 'State machine references a domain id that does not exist in manifest.domains[].',
    run: missingFsmCoverage,
  },
];

const SEVERITY_ORDER: LintSeverity[] = ['high', 'medium', 'low'];

/** True when `level` meets or exceeds `threshold` (high > medium > low). */
function meets(level: LintSeverity, threshold: LintSeverity): boolean {
  return SEVERITY_ORDER.indexOf(level) <= SEVERITY_ORDER.indexOf(threshold);
}

export function runLint(manifest: AtlasManifest, config: LintConfig = {}): LintReport {
  const failOn = config.failOn ?? 'high';
  const findings: LintFinding[] = [];

  for (const rule of LINT_RULES) {
    const enabled = config.enabled?.[rule.id] ?? rule.enabled;
    if (!enabled) continue;
    const severity = config.severity?.[rule.id] ?? rule.severity;
    for (const f of rule.run(manifest)) {
      findings.push({ ruleId: rule.id, severity, ref: f.ref, message: f.message });
    }
  }

  const bySeverity: Record<LintSeverity, LintFinding[]> = {
    high: [],
    medium: [],
    low: [],
  };
  for (const f of findings) bySeverity[f.severity].push(f);

  const ok = !findings.some((f) => meets(f.severity, failOn));
  return { findings, bySeverity, ok };
}
